import { useLazyQuery } from '@apollo/client';
import { Card } from 'antd';
import { map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  ROUTES,
  SORT,
  SORTING,
} from '../../../common/constants';
import { formatDate } from '../../../common/utils';
import CommonTable from '../../../components/CommonTable';
import SearchComponent from '../../../components/SearchComponent';
import SortDropdown from '../../../components/SortDropdown';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { RACKS } from '../graphql/Queries';
import RackMasterList from './RackMasterList';

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const initialSort = {
  field: 'name',
  order: 'ASC',
};

const RackMasterTable = () => {
  const history = useHistory();
  const [rackMasterList, setRackMasterList] = useState([]);
  const [rackMasterSearchTerm, setRackMasterSearchTerm] = useState('');
  const [isEmptyRackMasterList, setIsEmptyRackMasterList] = useState(false);
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isRackMasterLoading, setIsRackMasterLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);
  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);

  const {
    state: { isDesktop },
  } = useContext(AppContext);

  const [racks] = useLazyQuery(RACKS, {
    onCompleted: (response) => {
      if (isDesktop) {
        setRackMasterList([...response?.racks?.data]);
      } else {
        setRackMasterList([...rackMasterList, ...response?.racks?.data]);
      }
      if (response?.racks?.count === 0 && initialPaginationValue?.total === 0) {
        setIsEmptyRackMasterList(true);
      } else {
        setIsEmptyRackMasterList(false);
      }

      if (response?.racks?.data?.length < LIMIT) {
        setIsActive(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.racks?.count,
      };
      setPaginationProp(pagination);
      setIsRackMasterLoading(false);
      setIsFetchMoreOrderLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  useEffect(() => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsRackMasterLoading(true);
    racks({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: rackMasterSearchTerm,
          isArchived: false,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  }, []);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsRackMasterLoading(true);
    setSortedInfo(sorter);
    racks({
      variables: {
        filter: {
          ...initialFilter,
          skip,
          limit: pagination?.pageSize,
          search: rackMasterSearchTerm,
          isArchived: false,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setRackMasterSearchTerm(trimValue);
    setRackMasterList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsRackMasterLoading(true);
    racks({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          isArchived: false,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  const columns = [
    {
      title: 'SHELF NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      width: 250,
      align: 'left',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      className: 'max-width-column',
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      render: (_, record) => record?.name || '-',
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 250,
      ellipsis: true,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
    },
  ];

  const locale = {
    emptyText: isEmptyRackMasterList ? '' : <span />,
  };

  const handleOrderScroll = () => {
    if (!isRackMasterLoading) {
      const currentLength = rackMasterList?.length;
      setIsFetchMoreOrderLoading(true);
      racks({
        variables: {
          filter: {
            ...initialFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            search: rackMasterSearchTerm,
            isArchived: false,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialSort,
        },
      });
    }
  };

  const infiniteScrollRef = useInfiniteScroll(handleOrderScroll, isActive);
  const sort = [
    {
      label: SORTING?.PRODUCT_NAME_A_Z?.label,
      value: SORTING?.PRODUCT_NAME_A_Z?.value,
      field: SORTING?.PRODUCT_NAME_A_Z?.field,
      sort: SORTING?.PRODUCT_NAME_A_Z?.sort,
    },
    {
      label: SORTING?.PRODUCT_NAME_Z_A?.label,
      value: SORTING?.PRODUCT_NAME_Z_A?.value,
      field: SORTING?.PRODUCT_NAME_Z_A?.field,
      sort: SORTING?.PRODUCT_NAME_Z_A?.sort,
    },
  ];

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsRackMasterLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setRackMasterList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);

    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });
    }

    racks({
      variables: {
        filter: {
          ...initialFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: rackMasterSearchTerm,
          isArchived: false,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialSort,
      },
    });
  };

  return (
    <>
      <div
        className={`d-flex justify-end mb-16 flex-wrap ${
          !isDesktop ? 'full-width' : ''
        }`}
      >
        {!isDesktop && (
          <>
            <SearchComponent
              className="search-box-rack"
              query={query}
              setQuery={setQuery}
              getData={handleSearch}
            />
            <div className="ml-16 d-flex align-center">
              <SortDropdown
                isOpen={isAllSortPopoverOpen}
                setIsOpen={setIsAllSortPopoverOpen}
                options={map(sort, (item) => item)}
                sortedInfo={sortedInfo}
                setSortedInfo={setSortedInfo}
                onFinish={handleSort}
              />
            </div>
          </>
        )}
      </div>

      {isDesktop ? (
        <Card className="full-height-without-nav">
          <div
            className={`d-flex justify-end mb-16 flex-wrap ${
              !isDesktop ? 'full-width' : ''
            }`}
          >
            <SearchComponent
              className="search-box"
              query={query}
              setQuery={setQuery}
              getData={handleSearch}
            />
          </div>
          <CommonTable
            locale={locale}
            columns={columns}
            data={rackMasterList || []}
            loading={isRackMasterLoading}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(record) => record?.id}
            onRow={(record) => ({
              onClick: () => {
                history?.push(`${ROUTES?.RACK_MASTER}/view/${record?.id}`);
              },
            })}
          />
        </Card>
      ) : (
        <Card className="mobile-with-search-action-container-rack">
          <RackMasterList
            rackMasterList={rackMasterList}
            loading={isRackMasterLoading}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyRackMasterList={isEmptyRackMasterList}
          />
        </Card>
      )}
    </>
  );
};

export default RackMasterTable;
